import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  BoldOutlined,
  ItalicOutlined,
  LinkOutlined,
  PaperClipOutlined,
  OrderedListOutlined,
  PictureOutlined,
  TableOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { App, Button, Dropdown, Input, Modal, Space, theme, Tooltip, Typography } from 'antd';
import { Tiptap, useEditor, useTiptap, useTiptapState, type Editor } from '@tiptap/react';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import StarterKit from '@tiptap/starter-kit';
import type { RichText } from '@titularis/api-client';
import { api } from '../api';
import './editor-texto.css';

/**
 * Editor de texto rico do backoffice.
 *
 * O conteúdo canônico é o JSON do TipTap: é isso que sai daqui e é isso que o
 * backend guarda na versão da questão. O HTML que o aluno lê é gerado no
 * servidor, com o mesmo conjunto de extensões declarado abaixo — se as duas
 * listas divergirem, formatação escrita aqui some lá.
 *
 * A `Image` é a figura do enunciado (13/10/2026). O servidor só deixa chegar ao
 * aluno a figura enviada pela rota de imagens: uma imagem colada de outro site
 * aparece aqui e some lá, de propósito.
 */
const extensoes = [
  StarterKit.configure({ link: false }),
  Link.configure({ openOnClick: false }),
  Image,
  Table,
  TableRow,
  TableHeader,
  TableCell,
];

export interface EditorTextoProps {
  value?: RichText | null;
  onChange?: (valor: RichText | null) => void;
  /** Altura mínima da área de escrita; comentários pedem mais espaço. */
  minHeight?: number;
  placeholder?: string;
  disabled?: boolean;
}

/** Documento vazio: o editor sempre devolve ao menos um parágrafo. */
const estaVazio = (documento: RichText | null | undefined): boolean => {
  if (!documento) return true;
  const conteudo = (documento as { content?: { content?: unknown[] }[] }).content;
  return !conteudo || conteudo.every((no) => !no.content || no.content.length === 0);
};

/**
 * Citação interna: link para uma das abas do comentário da própria questão.
 *
 * O `href` é um fragmento reservado, e não esquema próprio: fragmento não
 * navega para lugar nenhum sozinho, então mesmo sem tratamento na tela é
 * inofensivo. Usa o Link que já existe (D-11), sem extensão nova de TipTap,
 * sem mudança de schema e sem migração.
 *
 * O mesmo prefixo está no `content.ts` do backend, que remove o `target` desses
 * links para eles nunca abrirem aba.
 */
const CITACAO_INTERNA = '#comentario:';

const ABAS_CITAVEIS = [
  { chave: 'legislation', rotulo: 'Legislação' },
  { chave: 'jurisprudence', rotulo: 'Jurisprudência' },
  { chave: 'doctrine', rotulo: 'Doutrina' },
] as const;

/**
 * Os atalhos, conferidos por `code` e não por `key`.
 *
 * No macOS, `Alt+1` produz `key === '¡'` e `Alt+2`, `'™'` - o teclado já aplicou
 * o modificador. `code` é a tecla física, então o atalho funciona igual nos dois
 * sistemas. Também é por isso que o `preventDefault` importa: sem ele, o Mac
 * escreveria o símbolo no texto além de abrir o diálogo.
 */
const ATALHO_LINK = 'Digit1';
/**
 * Largura do menu da citação, usada também para não deixá-lo sair da tela.
 *
 * Encolheu junto com o alinhamento à esquerda: centralizados, os rótulos
 * precisavam de folga dos dois lados para não parecerem tortos entre si.
 * Encostados na margem, a sobra vira espaço vazio à direita.
 *
 * O piso é "Jurisprudência", o mais longo dos três: o dígito e o rótulo somam
 * uns 130px, e o resto é o respiro do botão. Abaixo disto o nome quebraria.
 */
const LARGURA_DO_MENU = 176;
const ATALHO_CITACAO = 'Digit2';

export function EditorTexto({
  value,
  onChange,
  minHeight = 120,
  placeholder,
  disabled = false,
}: EditorTextoProps) {
  const { token } = theme.useToken();

  const editor = useEditor({
    extensions: extensoes,
    content: value ?? undefined,
    editable: !disabled,
    onUpdate: ({ editor: instancia }) => {
      const documento = instancia.getJSON() as RichText;
      // Devolver `null` em vez de um parágrafo em branco deixa claro para o
      // backend que o campo não foi preenchido — e campo vazio não vira aba.
      onChange?.(estaVazio(documento) ? null : documento);
    },
  });

  // O formulário carrega os dados depois da primeira renderização (é uma
  // consulta). Sincronizamos só quando o valor externo realmente difere, senão
  // cada tecla digitada reposicionaria o cursor no início.
  useEffect(() => {
    if (!editor) return;

    const atual = editor.getJSON();
    if (JSON.stringify(atual) === JSON.stringify(value ?? { type: 'doc' })) return;
    if (estaVazio(atual as RichText) && estaVazio(value)) return;

    editor.commands.setContent(value ?? '', { emitUpdate: false });
  }, [editor, value]);

  /*
   * `emitUpdate: false` é o que impede a edição fantasma.
   *
   * O `setEditable` do TipTap emite `update` por padrão, e este efeito roda na
   * montagem: sem o `false`, todo editor avisava o pai de uma alteração que
   * ninguém fez. Quem escuta esse aviso conclui que há rascunho - e o Salvar
   * da gaveta do comentário nascia ligado, num artigo intocado.
   */
  useEffect(() => {
    editor?.setEditable(!disabled, false);
  }, [editor, disabled]);

  const comandos = useComandosDeLink(editor);
  const figura = useFigura(editor);
  /*
   * Onde o menu da citacao aparece. Nulo quando fechado; a coordenada e de
   * tela, tirada do cursor de texto no momento em que o atalho foi acionado.
   */
  const [menuEm, setMenuEm] = useState<{ x: number; y: number } | null>(null);
  const { destino, aoPassarOMouse, aoTirarOMouse } = useDestinoSobOMouse();

  const fecharMenu = useCallback(() => setMenuEm(null), []);
  const escolherCitacao = useCallback(
    (chave: string) => {
      comandos.citarComentario(chave);
      setMenuEm(null);
    },
    [comandos],
  );

  /**
   * Os atalhos do editor.
   *
   * Ficam no bloco inteiro, e nao so na area de texto: o `Alt+1` acionado com o
   * foco num botao da barra continua sendo o mesmo comando, e nao ha razao para
   * ele falhar por causa de onde o foco parou.
   */
  const aoTeclar = (evento: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled || !evento.altKey || evento.ctrlKey || evento.metaKey) return;
    if (evento.code !== ATALHO_LINK && evento.code !== ATALHO_CITACAO) return;

    /*
     * Sem isto o macOS escreve o simbolo do `Option+1` no texto junto de abrir o
     * comando - `Alt` e tecla morta la, nao so modificador.
     */
    evento.preventDefault();

    if (evento.code === ATALHO_LINK) {
      comandos.inserirLink();
      return;
    }

    /*
     * O menu nasce onde o texto esta, e nao onde a barra esta: a barra fica no
     * topo da caixa, que e justamente o que o atalho veio evitar alcancar.
     * `coordsAtPos` da o retangulo do cursor em coordenadas de tela.
     */
    const { to } = editor!.state.selection;
    const retangulo = editor!.view.coordsAtPos(to);
    setMenuEm({ x: retangulo.left, y: retangulo.bottom });
  };

  if (!editor) return null;

  // O CSS do editor lê estas variáveis: assim a folha de estilo alcança a área
  // editável do ProseMirror sem abrir mão dos tokens do tema.
  const variaveis = {
    '--editor-altura-minima': `${minHeight}px`,
    '--editor-raio': `${token.borderRadius}px`,
    '--editor-cor-borda': token.colorBorder,
    '--editor-cor-divisor': token.colorSplit,
    '--editor-cor-fundo': disabled ? token.colorBgContainerDisabled : token.colorBgContainer,
    '--editor-cor-cabecalho': token.colorFillTertiary,
    '--editor-cor-secundaria': token.colorTextSecondary,
    '--editor-cor-foco': token.colorPrimary,
    '--editor-sombra-foco': token.controlOutline,
  } as CSSProperties;

  return (
    <Tiptap editor={editor}>
      <div
        className={`titularis-editor${disabled ? ' titularis-editor--desabilitado' : ''}`}
        style={variaveis}
        onKeyDown={aoTeclar}
      >
        <BarraDeFerramentas
          disabled={disabled}
          comandos={comandos}
          onFigura={figura.escolher}
          enviandoFigura={figura.enviando}
        />
        <div
          className="titularis-editor__area"
          // Clicar na margem também escreve: a área editável ocupa a altura,
          // mas o preenchimento em volta dela não é do ProseMirror.
          onClick={(evento) => {
            if (aoClicarNoLink(evento)) return;
            if (!disabled) editor.chain().focus().run();
          }}
          // Os dois eventos borbulham do texto do ProseMirror, então um ouvinte
          // na área alcança todos os links sem o editor precisar saber deles.
          onMouseOver={aoPassarOMouse}
          onMouseOut={aoTirarOMouse}
        >
          <Tiptap.Content aria-label={placeholder} />
        </div>

        {menuEm ? (
          <MenuDeCitacao posicao={menuEm} onFechar={fecharMenu} onEscolher={escolherCitacao} />
        ) : null}

        {destino ? <DestinoDoLink destino={destino} /> : null}
        {figura.elementos}
      </div>
    </Tiptap>
  );
}

/**
 * Esquemas que um clique pode abrir.
 *
 * A lista existe porque o documento nem sempre foi escrito aqui: o Cadastro
 * Assistido também escreve, e `javascript:` num `window.open` seria código nosso
 * mandando executar código de terceiro. Endereço de outro esquema continua
 * visível no balão - só não é seguido.
 */
const ESQUEMAS_QUE_ABREM = ['http:', 'https:', 'mailto:'];

/**
 * Clicar no link leva ao endereço, em **outra aba**.
 *
 * Outra aba de propósito: na mesma, o clique tiraria o administrador de um
 * formulário com alterações que ainda não viraram versão.
 *
 * A citação interna não abre nada - `#comentario:` é convenção nossa, e não
 * endereço -, mas também não deixa o navegador seguir o fragmento: com o editor
 * só de leitura, o clique num link é navegação de verdade.
 *
 * Devolve se o clique foi num link, para o editor não disputar o foco com a aba
 * que acabou de abrir.
 */
function aoClicarNoLink(evento: React.MouseEvent<HTMLElement>): boolean {
  const link = (evento.target as HTMLElement).closest?.('a');
  const href = link?.getAttribute('href');
  if (!href) return false;

  evento.preventDefault();
  if (href.startsWith(CITACAO_INTERNA)) return true;

  try {
    const destino = new URL(href, window.location.href);
    if (ESQUEMAS_QUE_ABREM.includes(destino.protocol)) {
      window.open(destino.href, '_blank', 'noopener,noreferrer');
    }
  } catch {
    // Endereço que nem vira URL não leva a lugar nenhum; o balão já o mostra.
  }

  return true;
}

/** O link sob o mouse: o endereço e onde ele está na tela. */
interface DestinoSobOMouse {
  href: string;
  x: number;
  y: number;
  largura: number;
  altura: number;
}

/**
 * O link sob o mouse, para a tela poder dizer para onde ele vai.
 *
 * Quem escreve não tem como conferir o destino de um link já inserido: o texto
 * mostra a palavra, e o endereço só reaparece abrindo o diálogo de link com o
 * cursor dentro dele - uma conferência que custa um clique por palavra. No app
 * do aluno o clique responde; aqui o clique é para editar (`openOnClick: false`),
 * então quem responde é o mouse parado.
 *
 * O retângulo é o da **primeira linha** do link: num link que quebra em duas, a
 * caixa inteira envolve as duas linhas e o balão apareceria longe da palavra.
 */
function useDestinoSobOMouse() {
  const [destino, setDestino] = useState<DestinoSobOMouse | null>(null);

  const aoPassarOMouse = useCallback((evento: React.MouseEvent<HTMLDivElement>) => {
    const link = (evento.target as HTMLElement).closest?.('a');
    const href = link?.getAttribute('href');
    if (!link || !href) {
      setDestino(null);
      return;
    }

    const retangulo = link.getClientRects()[0] ?? link.getBoundingClientRect();
    const proximo = {
      href,
      x: retangulo.left,
      y: retangulo.top,
      largura: retangulo.width,
      altura: retangulo.height,
    };

    /*
     * Só troca quando muda de verdade: um link com negrito dentro dispara o
     * evento de novo a cada pedaço, e um objeto novo a cada disparo redesenharia
     * o balão embaixo do mouse parado.
     */
    setDestino((atual) =>
      atual && atual.href === href && atual.x === proximo.x && atual.y === proximo.y
        ? atual
        : proximo,
    );
  }, []);

  const aoTirarOMouse = useCallback((evento: React.MouseEvent<HTMLDivElement>) => {
    // Sair para dentro do próprio link (de um pedaço para o outro) não é sair.
    const indo = evento.relatedTarget as Node | null;
    const link = (evento.target as HTMLElement).closest?.('a');
    if (link && indo instanceof Node && link.contains(indo)) return;
    setDestino(null);
  }, []);

  /*
   * Rolar leva o texto e deixaria o balão para trás: ele está preso a uma
   * coordenada de tela, não ao parágrafo.
   */
  useEffect(() => {
    if (!destino) return;

    const fechar = () => setDestino(null);
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    return () => {
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
    };
  }, [destino]);

  return { destino, aoPassarOMouse, aoTirarOMouse };
}

/**
 * O balão que mostra o destino do link.
 *
 * **Tooltip, e não Popover nem diálogo.** A pergunta é "para onde isto vai" e a
 * resposta cabe numa linha: um balão com botões pediria mirar o mouse dentro
 * dele para usar o que oferece, e um diálogo interromperia a leitura para
 * responder o que se queria saber de passagem.
 *
 * A citação interna aparece pelo nome da aba, e não pelo `#comentario:` que está
 * no `href`: o prefixo é convenção nossa, e mostrá-lo cru convidaria a
 * digitá-lo. O endereço externo aparece como está - é o que se quer conferir.
 *
 * A âncora é um retângulo fixo, do tamanho do link, e sem eventos de ponteiro: o
 * balão do Antd precisa de um elemento para se apoiar, e o link mora dentro do
 * ProseMirror, onde não há componente React para envolver.
 */
function DestinoDoLink({ destino }: { destino: DestinoSobOMouse }) {
  const aba = destino.href.startsWith(CITACAO_INTERNA)
    ? ABAS_CITAVEIS.find((item) => item.chave === destino.href.slice(CITACAO_INTERNA.length))
    : undefined;

  const conteudo = destino.href.startsWith(CITACAO_INTERNA) ? (
    <Space size={6}>
      <PaperClipOutlined />
      {aba ? `Comentário: ${aba.rotulo}` : 'Comentário desta questão'}
    </Space>
  ) : (
    <Space size={6} align="start">
      <LinkOutlined />
      <span>
        <span style={{ wordBreak: 'break-all' }}>{destino.href}</span>
        {/*
          O convite fica escrito: clique que abre outra aba é comportamento que
          se descobre clicando, e descobrir clicando custa uma aba a cada engano.
        */}
        <span style={{ display: 'block', marginTop: 2, fontSize: 11, opacity: 0.75 }}>
          Clique para abrir em outra aba
        </span>
      </span>
    </Space>
  );

  return (
    <Tooltip open placement="top" title={conteudo}>
      <span
        aria-hidden
        style={{
          position: 'fixed',
          left: destino.x,
          top: destino.y,
          width: destino.largura,
          height: destino.altura,
          pointerEvents: 'none',
        }}
      />
    </Tooltip>
  );
}

/**
 * Os dois comandos de link, num lugar so.
 *
 * A barra e os atalhos de teclado chamam daqui. Escritos duas vezes, um dia o
 * botao e o atalho passariam a fazer coisas diferentes - e o que diverge
 * primeiro e sempre o caso de borda, como o que fazer quando nao ha selecao.
 */
function useComandosDeLink(editor: Editor | null) {
  const { token } = theme.useToken();
  const { modal } = App.useApp();

  /** Link externo: precisa de um endereco digitado, entao pede dialogo. */
  const inserirLink = useCallback(() => {
    if (!editor) return;

    const href = (editor.getAttributes('link').href as string | undefined) ?? '';
    /*
     * Citacao nao preenche o campo: mostrar `#comentario:legislation` num campo
     * que pede endereco convidaria a edita-lo a mao, e o prefixo e convencao
     * nossa, nao coisa para se digitar.
     */
    const atual = href.startsWith(CITACAO_INTERNA) ? '' : href;

    let destino = atual;
    const aplicar = () => {
      const endereco = destino.trim();
      if (!endereco) {
        editor.chain().focus().unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: endereco }).run();
    };

    const dialogo = modal.confirm({
      title: 'Endereco do link',
      content: (
        <input
          defaultValue={atual}
          placeholder="https://"
          aria-label="Endereco do link"
          /*
           * Foco automatico: o comando veio do teclado, e obrigar a pegar o
           * mouse para clicar no campo desfaria o atalho recem-usado.
           */
          autoFocus
          onChange={(evento) => (destino = evento.target.value)}
          /*
           * Enter aplica, como em qualquer campo de uma linha. Sem isto, quem
           * digita o endereco e aperta Enter nao ve nada acontecer.
           */
          onKeyDown={(evento) => {
            if (evento.key !== 'Enter') return;
            evento.preventDefault();
            aplicar();
            dialogo.destroy();
          }}
          style={{
            width: '100%',
            marginTop: 8,
            padding: '4px 8px',
            border: `1px solid ${token.colorBorder}`,
            borderRadius: token.borderRadius,
            background: token.colorBgContainer,
            color: token.colorText,
          }}
        />
      ),
      okText: 'Aplicar',
      cancelText: 'Cancelar',
      onOk: aplicar,
    });
  }, [editor, modal, token]);

  /**
   * Insere a citacao sobre o texto selecionado, ou escreve o nome da aba quando
   * nao ha selecao - "Conforme a legislacao" precisa de ancora visivel, e pedir
   * ao administrador que digite antes de acionar seria transformar um atalho em
   * duas etapas.
   */
  const citarComentario = useCallback(
    (chave: string) => {
      if (!editor) return;
      const aba = ABAS_CITAVEIS.find((item) => item.chave === chave);
      if (!aba) return;

      const href = `${CITACAO_INTERNA}${aba.chave}`;
      const { from, to } = editor.state.selection;

      if (from === to) {
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'text',
            text: aba.rotulo.toLowerCase(),
            marks: [{ type: 'link', attrs: { href } }],
          })
          // Sai da marca, senao tudo que se digitar depois continua sendo link.
          .unsetMark('link')
          .run();
        return;
      }

      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    },
    [editor],
  );

  /*
   * Memorizado porque o objeto vai como dependencia de efeito la em cima: novo
   * a cada render, ele remontaria os ouvintes de teclado do menu a cada tecla
   * digitada - e uma tecla pode cair no vao entre remover e adicionar.
   */
  return useMemo(() => ({ inserirLink, citarComentario }), [inserirLink, citarComentario]);
}

/**
 * O menu da citacao, ancorado no cursor de texto.
 *
 * **No cursor, e nao na barra.** O dropdown da barra continua existindo para
 * quem usa o mouse, mas o atalho existe justamente porque a barra fica longe num
 * comentario de quatro mil caracteres: abrir o menu la em cima seria devolver o
 * problema que o atalho veio resolver.
 *
 * **Nem dialogo.** Sobreposicao de tela cheia para escolher entre tres palavras
 * e interrupcao grande demais, e ainda obrigaria a tabular ate as opcoes.
 *
 * **Escolhe por digito, nao por setas.** Sao tres destinos fixos: com o numero
 * ao lado do nome, a operacao inteira sao duas teclas - `Alt+2` e `1` - e o
 * atalho se descobre lendo, em vez de virar folclore.
 *
 * `position: fixed` de proposito: o menu nasce de uma coordenada de viewport, e
 * assim nenhum ancestral com `overflow` o recorta.
 */
function MenuDeCitacao({
  posicao,
  onEscolher,
  onFechar,
}: {
  posicao: { x: number; y: number };
  onEscolher: (chave: string) => void;
  onFechar: () => void;
}) {
  const { token } = theme.useToken();
  const caixa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.preventDefault();
        onFechar();
        return;
      }

      const indice = ABAS_CITAVEIS.findIndex((_, ordem) => evento.code === `Digit${ordem + 1}`);
      if (indice >= 0) {
        evento.preventDefault();
        onEscolher(ABAS_CITAVEIS[indice]!.chave);
        return;
      }

      /*
       * Qualquer outra tecla fecha e segue, sem `preventDefault`: quem desistiu
       * e voltou a escrever nao deve perder o caractere digitado, nem precisar
       * apertar Esc antes de continuar a frase.
       */
      onFechar();
    };

    /*
     * Rolar ou redimensionar move o texto e deixaria o menu para tras: ele esta
     * presoa uma coordenada de tela, nao ao paragrafo.
     */
    /*
     * Clique fora fecha. O teste de conteudo importa: sem ele, o clique num
     * item do proprio menu desmontaria o menu antes de o item ser acionado, e
     * escolher com o mouse nunca funcionaria.
     */
    const aoClicar = (evento: MouseEvent) => {
      if (caixa.current?.contains(evento.target as Node)) return;
      onFechar();
    };

    window.addEventListener('keydown', aoTeclar, true);
    window.addEventListener('mousedown', aoClicar, true);
    window.addEventListener('scroll', onFechar, true);
    window.addEventListener('resize', onFechar);
    return () => {
      window.removeEventListener('keydown', aoTeclar, true);
      window.removeEventListener('mousedown', aoClicar, true);
      window.removeEventListener('scroll', onFechar, true);
      window.removeEventListener('resize', onFechar);
    };
  }, [onEscolher, onFechar]);

  return (
    <div
      ref={caixa}
      role="menu"
      aria-label="Citar um comentario desta questao"
      style={{
        position: 'fixed',
        /*
         * Encostado na borda direita, o menu sairia da tela: a conta o traz
         * para dentro em vez de deixa-lo cortado.
         */
        left: Math.min(posicao.x, window.innerWidth - LARGURA_DO_MENU - 8),
        top: posicao.y + 6,
        zIndex: 1050,
        width: LARGURA_DO_MENU,
        padding: 4,
        background: token.colorBgElevated,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadius,
        boxShadow: token.boxShadowSecondary,
      }}
    >
      {ABAS_CITAVEIS.map((aba, indice) => (
        <Button
          key={aba.chave}
          role="menuitem"
          type="text"
          block
          /*
           * `justifyContent`, e nao `textAlign`: o botao do Antd e `inline-flex`
           * com o conteudo centralizado, e alinhamento de texto nao move item de
           * flex - por isso o `textAlign: left` que estava aqui nao fazia nada.
           */
          style={{ justifyContent: 'flex-start', paddingInline: 8 }}
          /*
           * `onMouseDown` em vez de `onClick`: clicar num botao tira o foco do
           * editor antes do clique, e a selecao do texto se perde no caminho.
           */
          onMouseDown={(evento) => {
            evento.preventDefault();
            onEscolher(aba.chave);
          }}
        >
          <Space size={8}>
            <Typography.Text keyboard>{indice + 1}</Typography.Text>
            {aba.rotulo}
          </Space>
        </Button>
      ))}
    </div>
  );
}

/**
 * A barra reflete o estado do cursor: o botão de negrito fica marcado dentro de
 * um trecho em negrito. `useTiptapState` assina só o que interessa, para digitar
 * não redesenhar a barra inteira a cada tecla.
 */
function BarraDeFerramentas({
  disabled,
  comandos,
  onFigura,
  enviandoFigura,
}: {
  disabled: boolean;
  comandos: ReturnType<typeof useComandosDeLink>;
  onFigura: () => void;
  enviandoFigura: boolean;
}) {
  const { editor } = useTiptap();

  const ativos = useTiptapState((estado) => ({
    bold: estado.editor.isActive('bold'),
    italic: estado.editor.isActive('italic'),
    underline: estado.editor.isActive('underline'),
    bulletList: estado.editor.isActive('bulletList'),
    orderedList: estado.editor.isActive('orderedList'),
    blockquote: estado.editor.isActive('blockquote'),
    /*
     * Citação interna e link externo dividem a mesma marca `link`, então
     * `isActive('link')` acende os dois. Distinguir pelo `href` evita o botão
     * de link dizer "você está num link externo" quando se está numa citação.
     */
    link:
      estado.editor.isActive('link') &&
      !((estado.editor.getAttributes('link').href as string | undefined) ?? '').startsWith(
        CITACAO_INTERNA,
      ),
    citacao: ((estado.editor.getAttributes('link').href as string | undefined) ?? '').startsWith(
      CITACAO_INTERNA,
    ),
  }));

  if (!editor) return null;

  const botao = (
    chave: string,
    titulo: string,
    icone: React.ReactNode,
    acao: () => void,
    ativo = false,
  ) => (
    <Tooltip title={titulo} key={chave}>
      <Button
        size="small"
        type={ativo ? 'primary' : 'text'}
        disabled={disabled}
        aria-label={titulo}
        aria-pressed={ativo}
        icon={icone}
        onClick={acao}
      />
    </Tooltip>
  );

  return (
    <Space size={2} wrap className="titularis-editor__barra">
      {botao('bold', 'Negrito', <BoldOutlined />, () => editor.chain().focus().toggleBold().run(), ativos.bold)}
      {botao('italic', 'Itálico', <ItalicOutlined />, () => editor.chain().focus().toggleItalic().run(), ativos.italic)}
      {botao(
        'underline',
        'Sublinhado (Ctrl+U)',
        <UnderlineOutlined />,
        () => editor.chain().focus().toggleUnderline().run(),
        ativos.underline,
      )}
      {botao(
        'bullet',
        'Lista',
        <UnorderedListOutlined />,
        () => editor.chain().focus().toggleBulletList().run(),
        ativos.bulletList,
      )}
      {botao(
        'ordered',
        'Lista numerada',
        <OrderedListOutlined />,
        () => editor.chain().focus().toggleOrderedList().run(),
        ativos.orderedList,
      )}
      {botao(
        'quote',
        'Citação',
        <span aria-hidden style={{ fontWeight: 700 }}>&rdquo;</span>,
        () => editor.chain().focus().toggleBlockquote().run(),
        ativos.blockquote,
      )}
      {botao('link', 'Link (Alt+1)', <LinkOutlined />, comandos.inserirLink, ativos.link)}
      <Dropdown
        disabled={disabled}
        menu={{
          items: ABAS_CITAVEIS.map((aba) => ({ key: aba.chave, label: aba.rotulo })),
          onClick: ({ key }) => comandos.citarComentario(key),
        }}
      >
        <Tooltip title="Citar um comentário desta questão (Alt+2)">
          <Button
            type={ativos.citacao ? 'primary' : 'text'}
            size="small"
            disabled={disabled}
            aria-label="Citar comentário"
            icon={<PaperClipOutlined />}
          />
        </Tooltip>
      </Dropdown>
      {botao('table', 'Tabela', <TableOutlined />, () =>
        editor.chain().focus().insertTable({ rows: 3, cols: 2, withHeaderRow: true }).run(),
      )}
      <Tooltip title="Figura (mapa, gráfico ou diagrama da prova)">
        <Button
          size="small"
          type="text"
          disabled={disabled}
          loading={enviandoFigura}
          aria-label="Figura"
          icon={<PictureOutlined />}
          onClick={onFigura}
        />
      </Tooltip>
    </Space>
  );
}

/**
 * A figura do enunciado: escolher o arquivo, enviar, descrever e inserir.
 *
 * **O envio vem antes da descrição**, e não depois: a prévia do diálogo já é a
 * figura servida pela API, então quem revisa confere ali mesmo o que o aluno vai
 * ver - o recorte, a nitidez, se não é o arquivo errado.
 *
 * O lugar é guardado no clique: o diálogo tira o foco do editor, e inserir onde
 * o cursor estiver depois dele poria a figura no começo do texto. Cancelar deixa
 * no bucket um arquivo que ninguém cita, o que é barato e não aparece.
 */
function useFigura(editor: Editor | null) {
  const { message } = App.useApp();
  const seletor = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [pendente, setPendente] = useState<{ url: string; posicao: number } | null>(null);
  const [descricao, setDescricao] = useState('');

  const escolher = useCallback(() => seletor.current?.click(), []);

  const aoEscolher = async (evento: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = evento.target.files?.[0];
    // Zerar deixa escolher o mesmo arquivo de novo depois de um cancelamento.
    evento.target.value = '';
    if (!arquivo || !editor) return;

    const posicao = editor.state.selection.from;
    setEnviando(true);
    try {
      const { url } = await api.uploadQuestionImage(arquivo);
      setDescricao('');
      setPendente({ url, posicao });
    } catch (erro) {
      message.error((erro as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  const inserir = () => {
    if (!editor || !pendente) return;
    editor
      .chain()
      .focus()
      .insertContentAt(pendente.posicao, {
        type: 'image',
        attrs: { src: pendente.url, alt: descricao.trim() || null },
      })
      .run();
    setPendente(null);
  };

  const elementos = (
    <>
      <input
        ref={seletor}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        aria-label="Arquivo da figura"
        hidden
        onChange={aoEscolher}
      />
      <Modal
        open={pendente !== null}
        title="Inserir figura"
        okText="Inserir"
        cancelText="Cancelar"
        onOk={inserir}
        onCancel={() => setPendente(null)}
        destroyOnHidden
      >
        {pendente ? (
          <img src={pendente.url} alt="" className="titularis-editor__previa-da-figura" />
        ) : null}
        <Typography.Text type="secondary">
          Descrição para quem usa leitor de tela (texto alternativo)
        </Typography.Text>
        <Input.TextArea
          value={descricao}
          onChange={(evento) => setDescricao(evento.target.value)}
          placeholder="Ex.: Mapa do corredor bioceânico planejado"
          autoSize={{ minRows: 2, maxRows: 4 }}
          aria-label="Descrição da figura"
          style={{ marginTop: 6 }}
        />
      </Modal>
    </>
  );

  return { escolher, enviando, elementos };
}
